import { useState } from "react";
import {
  Check,
  Heart,
  PackageCheck,
  ShoppingCart,
  Star,
  Truck,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  RecommendationColor,
  RecommendationProduct,
} from "./RecommendationProductsData";
import { useCart } from "../../../context/CartContext";

type RecommendationProductCardProps = {
  product: RecommendationProduct;
};

const formatPrice = (value: number) =>
  new Intl.NumberFormat("en-IN").format(value);

const RecommendationProductCard = ({
  product,
}: RecommendationProductCardProps) => {
  const navigate = useNavigate();

  const { addToCart, isProductInCart } = useCart();

  const [wishlisted, setWishlisted] = useState(false);

  const [selectedColor, setSelectedColor] = useState(0);

  const addedToCart = isProductInCart(product.id);

  const discount = Math.round(
    ((product.mrp - product.price) / product.mrp) * 100,
  );

  const handleProductClick = () => {
    navigate(`/DetailPage/${product.id}`, {
      state: {
        product: {
          ...product,

          breadcrumb: ["Home", product.category, product.title],
        },
      },
    });
  };

  const handleAddToCart = () => {
    if (addedToCart) {
      return;
    }

    addToCart({
      productId: product.id,

      title: product.title,

      description: product.description,

      image: product.image,

      price: product.price,

      mrp: product.mrp,

      color: product.colors[selectedColor]?.name,
    });
  };

  return (
    <article
      className="recommendation-product-card"
      role="button"
      tabIndex={0}
      onClick={(event) => {
        const target = event.target as HTMLElement;

        if (target.closest("button")) {
          return;
        }

        handleProductClick();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();

          handleProductClick();
        }
      }}
    >
      <div className="recommendation-image-wrapper">
        <span className="recommendation-badge">{product.badge}</span>

        <button
          type="button"
          className={`recommendation-wishlist ${wishlisted ? "active" : ""}`}
          onClick={() => setWishlisted((prev) => !prev)}
          aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
        >
          <Heart
            size={17}
            strokeWidth={2}
            fill={wishlisted ? "currentColor" : "none"}
          />
        </button>

        <img
          src={product.image}
          alt={product.title}
          className="recommendation-image"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src =
              "/assets/images/logos/salarlogo.png";
          }}
        />
      </div>

      <div className="recommendation-info">
        {product.colors.length > 0 && (
          <div className="recommendation-colors">
            <div className="recommendation-color-list">
              {product.colors.map(
                (color: RecommendationColor, index: number) => (
                  <button
                    type="button"
                    key={color.name}
                    className={`recommendation-color ${
                      selectedColor === index ? "active" : ""
                    }`}
                    title={color.name}
                    aria-label={`Select ${color.name}`}
                    onClick={() => setSelectedColor(index)}
                  >
                    <span
                      style={{
                        backgroundColor: color.value,
                      }}
                    />
                  </button>
                ),
              )}
            </div>

            {product.extraColors > 0 && (
              <button type="button" className="recommendation-more-colors">
                +{product.extraColors} more
              </button>
            )}
          </div>
        )}

        <h3>{product.title}</h3>

        <p className="recommendation-description">{product.description}</p>

        <div className="recommendation-rating">
          <strong>{product.rating.toFixed(1)}</strong>

          <span className="recommendation-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={12}
                strokeWidth={1.8}
                fill={
                  star <= Math.round(product.rating) ? "currentColor" : "none"
                }
              />
            ))}
          </span>

          <span className="recommendation-review-count">
            ({product.reviews})
          </span>
        </div>

        <div className={`recommendation-deal ${product.dealType}`}>
          {product.deal}
        </div>

        <div className="recommendation-price">
          <strong>
            <sup>₹</sup>

            {formatPrice(product.price)}
          </strong>

          <span className="recommendation-discount">{discount}% OFF</span>
        </div>

        <div className="recommendation-mrp">
          <span>MRP</span>

          <del>₹{formatPrice(product.mrp)}</del>

          <strong>Save ₹{formatPrice(product.mrp - product.price)}</strong>
        </div>

        <div className={`recommendation-delivery ${product.deliveryType}`}>
          {product.deliveryType === "delivery" && <Truck size={14} />}

          {product.deliveryType === "fast" && <Zap size={14} />}

          {product.deliveryType === "stock" && <PackageCheck size={14} />}

          <span>{product.delivery}</span>
        </div>

        <button
          type="button"
          className={`recommendation-cart ${addedToCart ? "added" : ""}`}
          onClick={handleAddToCart}
        >
          {addedToCart ? (
            <Check size={15} strokeWidth={2.2} />
          ) : (
            <ShoppingCart size={15} strokeWidth={2} />
          )}

          {addedToCart ? "Added to Cart" : "Add to Cart"}
        </button>
      </div>
    </article>
  );
};

export default RecommendationProductCard;
